import ActionHandler from 'titus-ts/dist/js/Util/Actions/ActionHandler';
import Action from 'titus-ts/dist/js/Util/Actions/Action';
import { OfficeApi } from '../OfficeApi';
import FieldCodeService from 'titus-ts/dist/js/Util/Actions/FieldCodeService';
import ApplyHeaderFooterHandler from 'titus-ts/dist/js/Util/Actions/ApplyHeaderFooterHandler';
import {
    defaultHeaderFooterOptions,
    HeaderFooterOptions,
    HeaderFooterPage,
    PowerPointHeaderFooterTarget,
} from '../HeaderFooterOptions';

export default class OfficeApplyHeaderFooterHandler implements ActionHandler {
    private officeApi: OfficeApi;
    private fieldCodeService: FieldCodeService;
    private applyHeaderFooterHandler: ApplyHeaderFooterHandler;

    constructor(
        officeApi: OfficeApi,
        fieldCodeService: FieldCodeService,
        applyHeaderFooterHandler?: ApplyHeaderFooterHandler,
    ) {
        this.officeApi = officeApi;
        this.fieldCodeService = fieldCodeService;
        this.applyHeaderFooterHandler = applyHeaderFooterHandler || new ApplyHeaderFooterHandler();
    }

    public handle = async (actions: Action[]): Promise<void> => {
        const advancedActions = actions.filter((action) => action.type === 'AdvancedHeaderFooter');
        const legacyActions = actions.filter((action) => action.type === 'HTMLHeaderFooter');

        await this.handleLegacyActions(legacyActions);

        for (const action of advancedActions) {
            await this.handleAdvancedAction(action);
        }
    };

    private handleLegacyActions = async (actions: Action[]): Promise<void> => {
        if (actions.length === 0) return;

        const updatedActions = this.fieldCodeService.process(actions, 'HTMLHeaderFooter');
        const { header, footer } = this.applyHeaderFooterHandler.getHeaderFooter(updatedActions);
        const prefix = 'Titus';

        if (header) {
            await this.officeApi.setHeaderAndFooterAsync('header', header, prefix);
        }
        if (footer) {
            await this.officeApi.setHeaderAndFooterAsync('footer', footer, prefix);
        }
    };

    private handleAdvancedAction = async (action: Action): Promise<void> => {
        const content = this.fieldCodeService.replaceFieldCodes(action.params.get('Content') || '');
        if (!content) return;

        const location = action.params.get('HFLocation')?.trim().toLowerCase();
        const options = this.getAdvancedOptions(action.params);

        if (location === 'header' || location === 'header and footer') {
            await this.officeApi.setHeaderAndFooterAsync('header', content, options.bookmarkPrefix, options);
        }
        if (location === 'footer' || location === 'header and footer') {
            await this.officeApi.setHeaderAndFooterAsync('footer', content, options.bookmarkPrefix, options);
        }
    };

    private getAdvancedOptions = (parameters: Map<string, string>): HeaderFooterOptions => {
        const options = defaultHeaderFooterOptions(parameters.get('BookmarkPrefix')?.trim() || 'Titus');

        options.sections = parameters.get('Sections')?.trim() || 'All';
        options.removeMarkings = this.parseBoolean(parameters.get('RemoveMarkings'), false);
        options.retainFields = this.parseMultiSelect(parameters.get('RetainFields'));
        options.pages = this.parseMultiSelect(parameters.get('Pages')).filter(this.isHeaderFooterPage);
        options.insertAtStart = this.parseBoolean(parameters.get('InsertAtStart'), true);
        options.slidesAndPages = this.parseMultiSelect(parameters.get('SlidesAndPages')).filter(
            this.isPowerPointTarget,
        );
        if (options.slidesAndPages.length === 0) options.slidesAndPages = ['All slides'];
        options.horizontalPosition = this.parseNumber(parameters.get('HorizontalPosition'));
        options.verticalPosition = this.parseNumber(parameters.get('VerticalPosition'));

        return options;
    };

    private parseMultiSelect = (value: string | undefined): string[] =>
        (value || '')
            .split(';')
            .map((item) => item.trim())
            .filter(Boolean);

    private parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
        if (value?.toLowerCase() === 'true') return true;
        if (value?.toLowerCase() === 'false') return false;
        return defaultValue;
    };

    private parseNumber = (value: string | undefined): number => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    };

    private isHeaderFooterPage = (value: string): value is HeaderFooterPage =>
        value === 'First' || value === 'Odd' || value === 'Even';

    private isPowerPointTarget = (value: string): value is PowerPointHeaderFooterTarget =>
        value === 'All slides' ||
        value === 'First slide' ||
        value === 'Handout pages' ||
        value === 'Note pages' ||
        value === 'Title slide';
}
